import * as Popover from '@radix-ui/react-popover';
import { Info } from 'lucide-react';
import { useRef, useState } from 'react';
import { LearnMore } from './learn-more';

/**
 * ⓘ toggletip. Opens on hover, keyboard focus, click or Enter; the content can hold a "Learn more" link,
 * so it is a popover rather than a tooltip. Escape closes it.
 */
export function FieldHelp({ label, help, example, learnMore }: { label: string; help: string; example?: string; learnMore?: string }) {
  const [open, setOpen] = useState(false);
  const hoverTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const hover = (v: boolean) => {
    clearTimeout(hoverTimer.current);
    hoverTimer.current = setTimeout(() => setOpen(v), v ? 250 : 150);
  };
  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger
        type="button"
        aria-label={`About ${label}`}
        className="inline-grid size-5 place-items-center rounded-full text-muted-foreground hover:text-foreground"
        onPointerEnter={() => hover(true)}
        onPointerLeave={() => hover(false)}
        onFocus={() => setOpen(true)}
      >
        <Info className="size-3.5" aria-hidden="true" />
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          side="top"
          align="start"
          sideOffset={6}
          onOpenAutoFocus={e => e.preventDefault()}
          onPointerEnter={() => hover(true)}
          onPointerLeave={() => hover(false)}
          className="z-50 max-w-xs space-y-1.5 rounded-md border bg-surface-raised p-3 text-xs shadow-md"
        >
          <p className="font-medium">{label}</p>
          <p className="text-muted-foreground">{help}</p>
          {example && (
            <p>
              <span className="text-muted-foreground">Example: </span>
              <bdi dir="auto">{example}</bdi>
            </p>
          )}
          {learnMore && <LearnMore slug={learnMore} />}
          <Popover.Arrow className="fill-surface-raised" />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
